import { Component, OnInit, AfterViewInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { TemplateService, Question, QuestionPrompt } from '../services/template.service';

interface Task {
  name: string;
  description: string;
  icon: string;
  color: string;
  complexity?: string;
  templateId: string;
  questionId?: string;
  instructions: string;
  isEdit: boolean;
  prompts?: { id?: string; text: string }[];
}


@Component({
  selector: 'app-details',
  imports: [CommonModule, FormsModule, RouterLink],
  templateUrl: './details.component.html',
  styleUrl: './details.component.css'
})
export class DetailsComponent implements OnInit, AfterViewInit {
  currentTheme: 'light' | 'dark' = 'light';
  isLoadingData = false;
  templateId: string | null = null;
  templateName = '';
  selectedQuestionId: string | null = null;
  returnUrl: string | null = null;
  selectedTask = {
    name: '',
    templateId: '',
    instructions: ''
  };

  // Sidebar resize state
  sidebarWidth = 500;
  minSidebarWidth = 280;
  maxSidebarWidth = 500;
  isResizing = false;
  hoveredTask: Task | null = null;
  tooltipX = 0;
  tooltipY = 0;

  // Search/filter state
  searchQuery = '';

  tasks: Task[] = [];

  constructor(
    private route: ActivatedRoute,
    private router: Router,
private templateService: TemplateService
  ) {}

  ngOnInit() {
    this.initializeTheme();
    this.loadTaskFromRoute();
    this.loadSidebarWidth();
  }

  ngAfterViewInit() {
    this.initializeLucideIcons();
  }

  loadTaskFromRoute() {
    // Get return URL from navigation state
    const state = history.state;
    if (state?.returnUrl) {
      this.returnUrl = state.returnUrl;
    }

    // Check route params for /details/:templateId/:questionId format
    this.route.params.subscribe(params => {
      if (params['templateId']) {
        this.templateId = params['templateId'];
        const questionId = params['questionId'] ?? null;
        this.loadTemplateData(questionId);
      }
    });

    // Also check query params for templateid (matches API format: ?templateid=17853)
    this.route.queryParams.subscribe(params => {
      if (params['templateid'] && !this.templateId) {
        this.templateId = params['templateid'];
        this.loadTemplateData();
      }
      // Also support templateId (camelCase) for flexibility
      if (params['templateId'] && !this.templateId) {
        this.templateId = params['templateId'];
        this.loadTemplateData();
      }
      if (params['name']) {
        this.selectedTask.name = params['name'];
      }
      if (params['instructions']) {
        this.selectedTask.instructions = params['instructions'];
      }
    });
  }

  loadTemplateData(targetQuestionId: string | null = null) {
    if (!this.templateId) return;

    this.isLoadingData = true;
    
    // Try to get cached data first
    const cachedData = this.templateService.getCachedData(this.templateId);
    
    if (cachedData) {
      // Use cached data - no API call needed
      this.processTemplateData(cachedData, targetQuestionId);
    } else {
      // Fallback to API call if no cache (e.g., direct navigation to details page)
      this.templateService.getQuestions(this.templateId).subscribe({
        next: (data) => {
          this.templateService.setCachedData(data);
          this.processTemplateData(data, targetQuestionId);
        },
        error: (err) => {
          console.error('Failed to load template questions:', err);
          this.isLoadingData = false;
        }
      });
    }
  }

  private processTemplateData(data: any, targetQuestionId: string | null) {
    this.templateName = data.templateName;
    this.selectedTask.templateId = `TPL-${data.templateId}`;
    
    // Convert questions to tasks - prompts are included in the questions response
    this.tasks = data.questions.map((q: any, index: number) => ({
      name: q.name,
      description: `Question ID: ${q.id}`,
      icon: this.getIconForDataType(q.dataType),
      color: this.getColorForIndex(index),
      templateId: `TPL-${this.templateId}`,
      questionId: q.id,
      instructions: q.prompts && q.prompts.length > 0 ? q.prompts.map((p: any) => p.text).join('\n\n') : '',
      isEdit: q.prompts && q.prompts.length > 0,
      prompts: q.prompts || []
    }));

    // Select target question if provided, otherwise first task
    if (targetQuestionId) {
      const targetTask = this.tasks.find(t => t.questionId === targetQuestionId);
      if (targetTask) {
        this.selectedTask.name = targetTask.name;
        this.selectTask(targetTask);
      }
    } else if (this.tasks.length > 0) {
      this.selectedTask.name = this.tasks[0].name;
      this.selectTask(this.tasks[0]);
    }

    this.isLoadingData = false;
    setTimeout(() => this.initializeLucideIcons(), 0);
  }

  selectTask(task: Task) {
    this.selectedTask.name = task.name;
    this.selectedTask.templateId = task.templateId;
    this.selectedQuestionId = task.questionId || null;
    
    // Use prompts already loaded in the task (prepopulate for edit)
    if (task.prompts && task.prompts.length > 0) {
      this.selectedTask.instructions = task.prompts.map(p => p.text).join('\n\n');
    } else {
      this.selectedTask.instructions = task.instructions || '';
    }
    
    // Reinitialize icons after selection
    setTimeout(() => this.initializeLucideIcons(), 0);
    setTimeout(() => this.initializeLucideIcons(), 100);
  }

  private getIconForDataType(dataType: string): string {
    const icons: { [key: string]: string } = {
      'Photo': 'camera',
      'Text': 'type',
      'Number': 'hash',
      'Date': 'calendar',
      'Checkbox': 'check-square'
    };
    return icons[dataType] || 'file';
  }

  private getColorForIndex(index: number): string {
    const colors = ['cyan', 'violet', 'rose', 'emerald', 'amber', 'blue'];
    return colors[index % colors.length];
  }

  initializeTheme() {
    const savedTheme = localStorage.getItem('theme') as 'light' | 'dark';
    if (savedTheme) {
      this.currentTheme = savedTheme;
      this.applyTheme(savedTheme);
    }
  }

  setTheme(theme: 'light' | 'dark') {
    this.currentTheme = theme;
    this.applyTheme(theme);
    localStorage.setItem('theme', theme);
  }

  private applyTheme(theme: 'light' | 'dark') {
    if (theme === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }

  goBack() {
    if (this.returnUrl) {
      this.router.navigateByUrl(this.returnUrl);
    } else {
      this.router.navigate(['/']);
    }
  }

  getTaskShortId(templateId: string): string {
    return templateId.split('-')[1] || '8291';
  }

  private initializeLucideIcons() {
    if (typeof (window as any).lucide !== 'undefined') {
      (window as any).lucide.createIcons({
        attrs: {
          'stroke-width': 1.5
        }
      });
    }
  }

  // Sidebar resize methods
  loadSidebarWidth() {
    const saved = localStorage.getItem('sidebarWidth');
    if (saved) {
      this.sidebarWidth = Math.max(this.minSidebarWidth, Math.min(this.maxSidebarWidth, parseInt(saved, 10)));
    }
  }

  startResize(event: MouseEvent) {
    this.isResizing = true;
    event.preventDefault();
    document.addEventListener('mousemove', this.onResize.bind(this));
    document.addEventListener('mouseup', this.stopResize.bind(this));
  }

  onResize(event: MouseEvent) {
    if (!this.isResizing) return;
    const newWidth = event.clientX;
    this.sidebarWidth = Math.max(this.minSidebarWidth, Math.min(this.maxSidebarWidth, newWidth));
  }

  stopResize() {
    this.isResizing = false;
    localStorage.setItem('sidebarWidth', this.sidebarWidth.toString());
    document.removeEventListener('mousemove', this.onResize.bind(this));
    document.removeEventListener('mouseup', this.stopResize.bind(this));
  }

  // Tooltip methods
  showTooltip(task: Task, event: MouseEvent) {
    this.hoveredTask = task;
    this.tooltipX = event.clientX + 16;
    this.tooltipY = event.clientY - 10;
  }

  hideTooltip() {
    this.hoveredTask = null;
  }

  updateTooltipPosition(event: MouseEvent) {
    if (this.hoveredTask) {
      this.tooltipX = event.clientX + 16;
      this.tooltipY = event.clientY - 10;
    }
  }

  // Filtered tasks based on search query
  get filteredTasks(): Task[] {
    if (!this.searchQuery.trim()) {
      return this.tasks;
    }
    const query = this.searchQuery.toLowerCase().trim();
    return this.tasks.filter(task => 
      task.name.toLowerCase().includes(query) ||
      task.questionId?.toString().includes(query) ||
      task.prompts?.some(p => p.text.toLowerCase().includes(query))
    );
  }
}
